import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, Loader2, ClipboardList, ArrowLeft } from 'lucide-react';

const BADGE_CLS = {
  official_form: 'bg-green-100 text-green-800',
  official_page: 'bg-blue-100 text-blue-800',
  listing_only: 'bg-amber-100 text-amber-800',
};

const EMPTY = {
  name: '', abbr: '', category: '', subcategory: '', access_label: 'listing_only',
  official_url: '', official_source: '', developer: '', copyright_status: '',
  classification: 'Screening tool', population: 'Adults', administration: 'Self-report',
  purpose: '', warning: '', note: '', redistribution_confirmed: false, status: 'Published', published: true,
};

const BehavioralHealthAdmin = () => {
  const [data, setData] = useState({ instruments: [], categories: [], access_labels: {} });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API_URL}/admin/behavioral-health`); setData(r.data); }
    catch (e) { toast.error(e.response?.status === 403 ? 'Admin access required.' : 'Could not load.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { document.title = 'Behavioral Health Admin | INASW'; load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (inst) => {
    setEditing(inst.instrument_id);
    setForm({ ...EMPTY, ...inst, age_group: undefined });
    setOpen(true);
  };

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.name.trim() || !form.category) { toast.error('Name and category are required.'); return; }
    setSaving(true);
    const payload = { ...form };
    delete payload.instrument_id; delete payload.age_group; delete payload._id;
    try {
      if (editing) await axios.put(`${API_URL}/admin/behavioral-health/${editing}`, payload);
      else await axios.post(`${API_URL}/admin/behavioral-health`, payload);
      toast.success(editing ? 'Updated.' : 'Instrument added.');
      setOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save.'); } finally { setSaving(false); }
  };

  const del = async (inst) => {
    if (!window.confirm(`Delete "${inst.name}"?`)) return;
    try { await axios.delete(`${API_URL}/admin/behavioral-health/${inst.instrument_id}`); toast.success('Deleted.'); await load(); }
    catch (e) { toast.error('Could not delete.'); }
  };

  const filtered = data.instruments.filter((i) => !search || `${i.name} ${i.abbr} ${i.subcategory}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/admin" className="text-sm text-gray-500 hover:text-primary flex items-center gap-1 mb-3"><ArrowLeft className="w-4 h-4" /> Admin Dashboard</Link>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <p className="text-secondary font-semibold uppercase tracking-widest text-xs flex items-center gap-2"><ClipboardList className="w-4 h-4" /> Directory Management</p>
            <h1 className="font-serif text-3xl font-bold text-primary" data-testid="bh-admin-title">Behavioral Health Instruments</h1>
          </div>
          <Button className="bg-primary text-white" onClick={openNew} data-testid="bh-admin-add"><Plus className="w-4 h-4 mr-1" /> Add Instrument</Button>
        </div>

        <Input placeholder="Search instruments…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-sm mb-4" data-testid="bh-admin-search" />

        {loading ? (
          <div className="text-center py-16 text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : (
          <div className="bg-white rounded-2xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-left"><tr>
                <th className="px-4 py-3">Instrument</th><th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Access</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {filtered.map((i) => (
                  <tr key={i.instrument_id} className="border-t" data-testid={`bh-admin-row-${i.instrument_id}`}>
                    <td className="px-4 py-3"><span className="font-medium text-gray-800">{i.name}</span> {i.abbr && <span className="text-xs text-secondary font-semibold">({i.abbr})</span>}<div className="text-xs text-gray-400">{i.subcategory}</div></td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{data.categories.find((c) => c.slug === i.category)?.label || i.category}</td>
                    <td className="px-4 py-3"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${BADGE_CLS[i.access_label] || ''}`}>{data.access_labels[i.access_label]?.label || i.access_label}</span></td>
                    <td className="px-4 py-3 text-xs text-gray-500">{i.status}{i.published ? '' : ' (hidden)'}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openEdit(i)} className="text-gray-400 hover:text-primary p-1" data-testid={`bh-edit-${i.instrument_id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => del(i)} className="text-gray-400 hover:text-red-600 p-1" data-testid={`bh-delete-${i.instrument_id}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No instruments.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Instrument' : 'Add Instrument'}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label className="text-xs">Name *</Label><Input value={form.name} onChange={(e) => setF('name', e.target.value)} data-testid="bh-form-name" /></div>
            <div><Label className="text-xs">Abbreviation</Label><Input value={form.abbr} onChange={(e) => setF('abbr', e.target.value)} data-testid="bh-form-abbr" /></div>
            <div><Label className="text-xs">Category *</Label>
              <select value={form.category} onChange={(e) => setF('category', e.target.value)} className="w-full border rounded-md h-10 px-2 text-sm" data-testid="bh-form-category">
                <option value="">Select…</option>
                {data.categories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Subcategory / condition</Label><Input value={form.subcategory} onChange={(e) => setF('subcategory', e.target.value)} data-testid="bh-form-subcategory" /></div>
            <div><Label className="text-xs">Access label</Label>
              <select value={form.access_label} onChange={(e) => setF('access_label', e.target.value)} className="w-full border rounded-md h-10 px-2 text-sm" data-testid="bh-form-access">
                <option value="official_form">Official Form Available (green)</option>
                <option value="official_page">Official Access Page (blue)</option>
                <option value="listing_only">Listing Only (gold)</option>
              </select>
            </div>
            <div className="sm:col-span-2"><Label className="text-xs">Official URL (verified only)</Label><Input value={form.official_url} onChange={(e) => setF('official_url', e.target.value)} placeholder="https://…" data-testid="bh-form-url" /></div>
            <div><Label className="text-xs">Official source / publisher</Label><Input value={form.official_source} onChange={(e) => setF('official_source', e.target.value)} data-testid="bh-form-source" /></div>
            <div><Label className="text-xs">Copyright / access status</Label><Input value={form.copyright_status} onChange={(e) => setF('copyright_status', e.target.value)} /></div>
            <div><Label className="text-xs">Classification</Label><Input value={form.classification} onChange={(e) => setF('classification', e.target.value)} /></div>
            <div><Label className="text-xs">Population</Label><Input value={form.population} onChange={(e) => setF('population', e.target.value)} /></div>
            <div><Label className="text-xs">Administration</Label><Input value={form.administration} onChange={(e) => setF('administration', e.target.value)} /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Purpose</Label><Textarea rows={2} value={form.purpose} onChange={(e) => setF('purpose', e.target.value)} data-testid="bh-form-purpose" /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Warning</Label><Textarea rows={2} value={form.warning} onChange={(e) => setF('warning', e.target.value)} /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Note</Label><Textarea rows={2} value={form.note} onChange={(e) => setF('note', e.target.value)} /></div>
            <div className="sm:col-span-2 flex items-center gap-2">
              <Checkbox id="redis" checked={form.redistribution_confirmed} onCheckedChange={(v) => setF('redistribution_confirmed', !!v)} data-testid="bh-form-redis" />
              <Label htmlFor="redis" className="text-xs text-gray-600">Redistribution confirmed (required to publish a downloadable "Official Form"; otherwise it is shown as an official access page).</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="pub" checked={form.published} onCheckedChange={(v) => setF('published', !!v)} data-testid="bh-form-published" />
              <Label htmlFor="pub" className="text-xs text-gray-600">Published (visible to public)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-primary text-white" onClick={save} disabled={saving} data-testid="bh-form-save">{saving ? 'Saving…' : (editing ? 'Save changes' : 'Add instrument')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BehavioralHealthAdmin;
